import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarDays, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATS = {
  football: { label: "Football", color: "bg-purple-600", text: "text-purple-300" },
  medical: { label: "Medical", color: "bg-red-600", text: "text-red-300" },
  holiday: { label: "Holiday", color: "bg-green-600", text: "text-green-300" },
  birthday: { label: "Birthday", color: "bg-pink-600", text: "text-pink-300" },
  deadline: { label: "Deadline", color: "bg-amber-600", text: "text-amber-300" },
};

export default function EventsCalendar() {
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", date: "", category: "football", notes: "" });

  const load = () => axios.get(`${API}/events`).then((r) => setEvents(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.title.trim() || !form.date) return toast.error("Title and date are required");
    await axios.post(`${API}/events`, form);
    toast.success("Event added to the calendar!");
    setForm({ title: "", date: "", category: "football", notes: "" });
    setOpen(false);
    load();
  };

  const remove = async (id) => {
    await axios.delete(`${API}/events/${id}`);
    toast.success("Event removed");
    load();
  };

  const grouped = events.reduce((acc, ev) => {
    const month = new Date(ev.date + "T12:00:00").toLocaleDateString("en-US", { month: "long", year: "numeric" });
    (acc[month] = acc[month] || []).push(ev);
    return acc;
  }, {});

  return (
    <section id="calendar" className="py-24 md:py-32 max-w-7xl mx-auto px-4 sm:px-6">
      <p className="font-sub text-xs tracking-[0.2em] uppercase font-bold text-purple-400 mb-3">Never Miss A Moment</p>
      <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight uppercase">
          Important <span className="text-purple-400">Dates</span>
        </h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button
              data-testid="add-event-btn"
              className="flex items-center gap-2 font-sub font-semibold border border-purple-500/50 hover:bg-purple-600 text-white px-6 py-3 rounded-full transition-colors duration-200 active:scale-95"
            >
              <Plus className="w-4 h-4" /> Add Event
            </button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-white/10 text-white">
            <DialogHeader>
              <DialogTitle className="font-sub">Add an Important Date</DialogTitle>
              <DialogDescription className="text-zinc-400">Games, appointments, holidays, birthdays, deadlines.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <Input data-testid="event-title-input" placeholder="Event title *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-zinc-800 border-white/10" />
              <div className="grid grid-cols-2 gap-4">
                <Input data-testid="event-date-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-zinc-800 border-white/10" />
                <select
                  data-testid="event-category-select"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="bg-zinc-800 border border-white/10 rounded-md px-3 text-sm h-9"
                >
                  {Object.entries(CATS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <Input data-testid="event-notes-input" placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-zinc-800 border-white/10" />
              <Button data-testid="event-submit-btn" onClick={submit} className="w-full bg-purple-600 hover:bg-purple-500 font-sub font-semibold">
                <CalendarDays className="w-4 h-4 mr-2" /> Add to Calendar
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-wrap gap-3 mb-10">
        {Object.entries(CATS).map(([k, v]) => (
          <span key={k} className="flex items-center gap-2 font-sub text-xs text-zinc-400">
            <span className={`w-2.5 h-2.5 rounded-full ${v.color}`} /> {v.label}
          </span>
        ))}
      </div>

      <div className="space-y-12" data-testid="events-list">
        {Object.entries(grouped).map(([month, evs]) => (
          <div key={month}>
            <h3 className="font-sub font-bold text-lg text-zinc-500 uppercase tracking-widest mb-6">{month}</h3>
            <div className="space-y-3">
              {evs.map((ev) => {
                const cat = CATS[ev.category] || CATS.deadline;
                const d = new Date(ev.date + "T12:00:00");
                return (
                  <div key={ev.id} data-testid={`event-${ev.id}`} className="group flex items-center gap-5 rounded-2xl bg-zinc-900 border border-white/5 hover:border-purple-500/40 p-5 transition-colors duration-300">
                    <div className="flex flex-col items-center justify-center w-16 h-16 rounded-xl bg-zinc-950 border border-white/10 flex-shrink-0">
                      <span className="font-display text-2xl text-white leading-none">{d.getDate()}</span>
                      <span className="font-sub text-[10px] uppercase tracking-widest text-zinc-500">{d.toLocaleDateString("en-US", { weekday: "short" })}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h4 className="font-sub font-bold text-white">{ev.title}</h4>
                        <span className={`text-[10px] font-sub font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full ${cat.color} text-white`}>{cat.label}</span>
                      </div>
                      {ev.notes && <p className="text-sm text-zinc-400 mt-1 truncate">{ev.notes}</p>}
                    </div>
                    <button
                      data-testid={`event-delete-${ev.id}`}
                      onClick={() => remove(ev.id)}
                      className="opacity-0 group-hover:opacity-100 p-2 rounded-lg text-zinc-500 hover:text-red-400 transition-[opacity,color] duration-200"
                      aria-label="Delete event"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
